import { basicExtensions } from '@modules/editor';
import { chatImagePasteExtension } from '@modules/editor/extensions/chatImagePaste';
import { codeblockExtension } from '@modules/editor/extensions/codeblock';
import { emojiSearchExtension } from '@modules/editor/extensions/emojiSearch';
import { slashCommandExtension } from '@modules/editor/extensions/slashCommand';
import { userMentionExtension } from '@modules/editor/extensions/userMention';
import type { EditorOptions } from '@tiptap/core';
import { useMemo } from 'react';
import { shallowEqual } from 'react-redux';
import type { IWorkspaceUser } from 'src/@types/workspace-info';

import { useSelector } from 'src/redux/store';

type Props = {
  participants: IWorkspaceUser[];
  handleChatUploadFile?: (file: File) => void;
  placeholder?: string;
  enableSpaceForAi?: boolean;
  enableSlashCommand?: boolean;
  enableCodeBlock?: boolean;
};

export const useConversationEditorConfig = ({
  handleChatUploadFile,
  participants,
  placeholder = "Write a message or press 'space' for AI. Use @ to quote a user...",
  enableSpaceForAi = true,
  enableSlashCommand = true,
  enableCodeBlock = true,
}: Props) => {
  const workspaceUsers = useSelector(
    (state) => state.workspace.workspace.users,
    shallowEqual,
  );

  const extensions = useMemo(() => {
    const items = [
      ...basicExtensions,
      userMentionExtension,
      emojiSearchExtension,
      chatImagePasteExtension,
    ];

    if (enableSlashCommand) {
      items.push(slashCommandExtension);
    }

    if (enableCodeBlock) {
      items.push(codeblockExtension);
    }

    return items;
  }, [enableSlashCommand, enableCodeBlock]);

  const editorConfig = useMemo(() => {
    return {
      extensions,
      content: '',
      extensionConfig: {
        enterBehave: {
          createNewLine: false,
        },
        aiPromptOptions: {
          enableSpaceForAi: enableSpaceForAi,
          position: 'fixed',
        },
        placeholder,
        userMention: {
          enabled: true,
          getUserById(id) {
            if (!workspaceUsers?.length) return null;
            const item = workspaceUsers?.find((user) => user._id === id);
            if (!item) return null;
            return {
              _id: item._id,
              picture: item.picture,
              name: item.name,
              status: item.status,
            };
          },
          async findUsers(search) {
            if (!participants?.length) return [];
            return participants?.filter((item) =>
              item.name
                .toLocaleLowerCase()
                .includes(search.toLocaleLowerCase()),
            );
          },
        },
        chatUploadFile: handleChatUploadFile,
      },
    } satisfies Partial<EditorOptions>;
  }, [
    workspaceUsers,
    participants,
    handleChatUploadFile,
    placeholder,
    enableSpaceForAi,
    extensions,
  ]);

  return { editorConfig };
};
